import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calculatePAC, calculatePOD } from '../utils/ingredientCalculations';

const USDA_API_KEY = process.env.REACT_APP_USDA_API_KEY || 'DEMO_KEY';
const USDA_BASE_URL = 'https://api.nal.usda.gov/fdc/v1';

const USDAImporter = ({ open, onClose, onImport }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [foodDetails, setFoodDetails] = useState(null);
  const [dataTypeFilter, setDataTypeFilter] = useState('');

  // Filtros de tipo de datos USDA
  const dataTypes = [
    { value: '', label: 'All Types' },
    { value: 'Foundation', label: 'Foundation Foods' },
    { value: 'SR Legacy', label: 'SR Legacy' },
    { value: 'Survey (FNDDS)', label: 'Survey (FNDDS)' },
    { value: 'Branded', label: 'Branded Foods' }
  ];

  // Buscar alimentos en USDA
  const searchFoods = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      let url = `${USDA_BASE_URL}/foods/search?query=${encodeURIComponent(searchQuery)}&pageSize=20&api_key=${USDA_API_KEY}`;
      
      // Agregar filtro de tipo de datos si está seleccionado
      if (dataTypeFilter) {
        url += `&dataType=${encodeURIComponent(dataTypeFilter)}`;
      }
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      setSearchResults(data.foods || []);
      
      if (!data.foods || data.foods.length === 0) {
        setError(t('no_results_found') || 'No results found. Try a different search term.');
      }
    } catch (err) {
      setError(t('usda_search_error') || 'Error searching USDA database');
      console.error('USDA API Error:', err);
    }
    
    setLoading(false);
  };

  // Obtener detalles nutricionales de un alimento
  const getFoodDetails = async (fdcId) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(
        `${USDA_BASE_URL}/food/${fdcId}?api_key=${USDA_API_KEY}`
      );
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      setFoodDetails(data);
      setSelectedFood(data);
    } catch (err) {
      setError(t('usda_details_error') || 'Error getting food details');
      console.error('USDA API Error:', err);
    }
    
    setLoading(false);
  };


    // 🔄 Conversión a gramos según unidad
  const normalizeToGrams = (value, unit) => {
      if (value == null) return 0;
      switch (unit.toLowerCase()) {
      case 'g': return value;
      case 'mg': return value / 1000;
      case 'µg':
      case 'mcg': return value / 1_000_000;
      case 'iu': return 0; // IU no tiene conversión directa a g sin contexto
      default: return value; // fallback
      }
  };

  const convertUSDAToIngredient = (usdaFood) => {
    const nutrients = {};

    // Mapeo USDA -> nuestros campos
    const nutrientMap = {
        '203': 'proteins',     // g
        '204': 'fats',         // g
        '205': 'carbohydrates',// g
        '269': 'sugars',       // g
        '291': 'fiber',        // g
        '255': 'water',        // g
        '301': 'calcium',      // mg
        '303': 'iron',         // mg
        '307': 'sodium',       // mg
        '324': 'vitamin_d',    // µg
        '401': 'vitamin_c',    // mg
        '415': 'vitamin_b6',   // mg
        '418': 'vitamin_b12',  // µg
        '221': 'alcohol',      // g

        // Azúcares específicos
        '210': 'sucrose',      // g
        '211': 'glucose',      // g
        '212': 'fructose',     // g
        '213': 'lactose',      // g
        '214': 'maltose',      // g
        '287': 'galactose',    // g

        // Minerales adicionales
        '304': 'magnesium',    // mg
        '305': 'phosphorus',   // mg
        '306': 'potassium',    // mg
        '309': 'zinc',         // mg
        '207': 'ash',          // g
    };

    // Procesar nutrientes
    if (usdaFood.foodNutrients) {
      console.log(usdaFood.foodNutrients);
        usdaFood.foodNutrients.forEach(nutrient => {
          const nutrientNumber = nutrient.nutrient?.number?.toString() || nutrient.nutrientNumber?.toString();
          const amount = nutrient.value || nutrient.amount;
          const unit = nutrient?.unitName || nutrient.nutrient.unitName || 'g';
          console.log(nutrientNumber);
          const fieldName = nutrientMap[nutrientNumber];
          if (fieldName && amount != null) {
              nutrients[fieldName] = normalizeToGrams(amount, unit);
          }
        });
    }

    function roundToTwo(num) {
        return Math.round(num * 100) / 100;
    }

    // Valores básicos (ya en g)
    let water = roundToTwo(nutrients.water || 0);
    let fats = roundToTwo(nutrients.fats || 0);
    let proteins = roundToTwo(nutrients.proteins || 0);
    let carbohydrates = roundToTwo(nutrients.carbohydrates || 0);
    let alcohol = roundToTwo(nutrients.alcohol || 0);
    let fiber = roundToTwo(nutrients.fiber || 0);
    let ash = roundToTwo(nutrients.ash || 0);

    // Azúcares específicos
    let sucrose = roundToTwo(nutrients.sucrose || 0);
    let glucose = roundToTwo(nutrients.glucose || 0);
    let fructose = roundToTwo(nutrients.fructose || 0);
    let lactose = roundToTwo(nutrients.lactose || 0);
    let maltose = roundToTwo(nutrients.maltose || 0);
    let galactose = roundToTwo(nutrients.galactose || 0);

    let specificSugars = sucrose + glucose + fructose + lactose + maltose + galactose;
    let sugars = Math.max(specificSugars, nutrients.sugars || 0);

    // Minerales estimados (ya en g)
    let estimatedMinerals = ash > 0 ? ash : (
        (nutrients.calcium || 0) +
        (nutrients.iron || 0) +
        (nutrients.sodium || 0) +
        (nutrients.magnesium || 0) +
        (nutrients.phosphorus || 0) +
        (nutrients.potassium || 0) +
        (nutrients.zinc || 0)
    );

    // Sal (Na en g → g sal)
    let salt = roundToTwo((nutrients.sodium || 0) * 2.54);

    let other_solids = roundToTwo(fiber + estimatedMinerals);

    // Total de sólidos
    let total_solids = roundToTwo(fats + proteins + sugars + other_solids + salt);

    // Not water defined → estimar
    if (nutrients.water == null || typeof nutrients.water == 'undefined') {
        water = Math.max(0, 100 - roundToTwo(total_solids + alcohol));
    }

    // Ajuste de sólidos → normalizar a 100 g
    const totalSum = water + total_solids + alcohol;
    if (Math.abs(totalSum - 100) > 0.01) {
        // Reparto la diferencia entre agua y total_solids proporcionalmente
        const to_fix = totalSum - 100;
        water -= to_fix * (water / (water + total_solids));
        total_solids -= to_fix * (total_solids / (water + total_solids));
    }

    // Ajuste final de other_solids
    other_solids = roundToTwo(total_solids - (fats + proteins + sugars + salt));
    if (other_solids < 0) {
        other_solids = 0;
        total_solids = fats + proteins + sugars + salt;
    }
    total_solids = Math.min(100, roundToTwo(total_solids));

    // Si quedo diferencia la paso a water
    water = Math.max(0, 100 - (total_solids + alcohol));
    
    // MSNF (solo válido en lácteos)
    const category = guessCategory(usdaFood.description || '');
    let msnf = 0;
    if (/milk|cream|cheese|yogurt/i.test(category)) {
        msnf = proteins + lactose + estimatedMinerals;
    }

    // can_be_mix
    let can_be_mix = true;

    // can_be_inclusion 
    let can_be_inclusion = false;
    if (/nut|chocolate|flavour|fruit/i.test(category)) {
        can_be_inclusion = true;
    }

    // PAC y POD usando helper
    const pacPodData = {
      sucrose,
      glucose,
      fructose,
      lactose,
      maltose,
      galactose,
      sugars,
      alcohol,
      salt
    };

    let pac = calculatePAC(pacPodData);
    let pod = calculatePOD(pacPodData);

    // HF=CocoaFat*0.9 + CocoaSolids * 1.8 + OtherFat * 1.4

    return {
        name: usdaFood.description?.replace(/,.*$/, '') || '',
        category,
        sugars: +(sugars.toFixed(2)),
        fats: +(fats.toFixed(2)),
        milkfat: 0,
        proteins: +(proteins.toFixed(2)),
        other_solids: +(other_solids.toFixed(2)),
        total_solids: +(total_solids.toFixed(2)),
        water: +(water.toFixed(2)),
        pac: +(pac.toFixed(2)),
        pod: +(pod.toFixed(2)),
        msnf: +(msnf.toFixed(2)),
        lactose: +(lactose.toFixed(2)),
        alcohol: +(alcohol.toFixed(2)),
        salt: +(salt.toFixed(2)),
        stabilizer: 0,
        emulsifiers: 0,
        hardening_factor: 0,
        _usda_details: specificSugars > 0 ? {
            sucrose: +(sucrose.toFixed(2)),
            glucose: +(glucose.toFixed(2)),
            fructose: +(fructose.toFixed(2)),
            lactose: +(lactose.toFixed(2)),
            maltose: +(maltose.toFixed(2)),
            galactose: +(galactose.toFixed(2)),
            ash: +(ash.toFixed(2))
        } : null
    };
  };

  // Adivinar categoría basada en la descripción
  const guessCategory = (description) => {
    const desc = description.toLowerCase();
    
    // Productos lácteos
    if (desc.includes('milk') || desc.includes('cream') || desc.includes('butter') || 
        desc.includes('cheese') || desc.includes('yogurt') || desc.includes('whey') ||
        desc.includes('dairy') || desc.includes('lactose')) {
      return 'Dairy';
    }
    
    // Azúcares y edulcorantes
    if (desc.includes('sugar') || desc.includes('honey') || desc.includes('syrup') ||
        desc.includes('molasses') || desc.includes('maple') || desc.includes('corn syrup') ||
        desc.includes('glucose') || desc.includes('fructose') || desc.includes('sucrose') ||
        desc.includes('sweetener') || desc.includes('agave')) {
      return 'Sugar';
    }
    
    // Chocolate y cacao
    if (desc.includes('chocolate') || desc.includes('cocoa') || desc.includes('cacao')) {
      return 'Chocolate';
    }
    
    // Nueces y frutos secos
    if (desc.includes('nut') || desc.includes('almond') || desc.includes('walnut') ||
        desc.includes('pecan') || desc.includes('hazelnut') || desc.includes('pistachio') ||
        desc.includes('cashew') || desc.includes('peanut') || desc.includes('macadamia')) {
      return 'Nut';
    }
    
    // Aceites y grasas
    if (desc.includes('oil') || desc.includes('fat') || desc.includes('lard') ||
        desc.includes('shortening') || desc.includes('margarine')) {
      return 'Oil';
    }
    
    // Huevos
    if (desc.includes('egg')) {
      return 'Egg';
    }
    
    // Frutas y saborizantes
    if (desc.includes('fruit') || desc.includes('berry') || desc.includes('vanilla') ||
        desc.includes('extract') || desc.includes('flavor') || desc.includes('essence') ||
        desc.includes('apple') || desc.includes('banana') || desc.includes('orange') ||
        desc.includes('strawberry') || desc.includes('cherry') || desc.includes('lemon')) {
      return 'Flavour';
    }
    
    // Vegetales
    if (desc.includes('vegetable') || desc.includes('carrot') || desc.includes('beet') ||
        desc.includes('spinach') || desc.includes('tomato')) {
      return 'Vegetable';
    }
    
    // Alcoholes
    if (desc.includes('alcohol') || desc.includes('wine') || desc.includes('rum') ||
        desc.includes('whiskey') || desc.includes('vodka') || desc.includes('liqueur') ||
        desc.includes('brandy') || desc.includes('beer')) {
      return 'Alcohol';
    }
    
    // Estabilizantes y espesantes
    if (desc.includes('gellan') || desc.includes('xanthan') || desc.includes('guar') ||
        desc.includes('carrageenan') || desc.includes('agar') || desc.includes('pectin') ||
        desc.includes('starch') || desc.includes('cornstarch')) {
      return 'Stabilizer';
    }
    
    return 'Other';
  };

  const handleImport = () => {
    if (selectedFood) {
      const ingredient = convertUSDAToIngredient(selectedFood);
      // Remover detalles internos antes de enviar al formulario
      const { _usda_details, ...cleanIngredient } = ingredient;
      onImport(cleanIngredient);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedFood(null);
    setFoodDetails(null);
    setError('');
    setDataTypeFilter('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('import_from_usda') || 'Import from USDA Database'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label={t('search_ingredients') || 'Search ingredients'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchFoods()}
            disabled={loading}
            placeholder="e.g., milk, sugar, vanilla, chocolate..."
          />
          
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>{t('data_type') || 'Data Type'}</InputLabel>
              <Select
                value={dataTypeFilter}
                onChange={(e) => setDataTypeFilter(e.target.value)}
                label={t('data_type') || 'Data Type'}
                disabled={loading}
              >
                {dataTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              onClick={searchFoods}
              disabled={loading || !searchQuery.trim()}
              sx={{ minWidth: 120 }}
            >
              {loading ? <CircularProgress size={20} /> : (t('search') || 'Search')}
            </Button>
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {t('usda_search_tip')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {searchResults.length > 0 && !selectedFood && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('search_results') || 'Search Results'}
            </Typography>
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((food) => {
                const preview = convertUSDAToIngredient(food);
                const hasSpecificSugars = preview._usda_details && preview._usda_details.sucrose + preview._usda_details.glucose + preview._usda_details.fructose > 0;
                const hasAlcohol = preview.alcohol > 0;
                return (
                <ListItem key={food.fdcId} disablePadding>
                  <ListItemButton onClick={() => getFoodDetails(food.fdcId)}>
                    <ListItemText
                      primary={food.description}
                      secondary={
                        <Box>
                          <Chip 
                            size="small" 
                            label={food.dataType} 
                            sx={{ mr: 1 }} 
                          />
                          {food.brandOwner && (
                            <Chip 
                              size="small" 
                              label={food.brandOwner} 
                              variant="outlined" 
                            />
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Chip size="small" label={t('water') + ": " + preview.water + "%"} />
                          <Chip size="small" label={t('fats') + ": " + preview.fats + "%"} />
                          <Chip size="small" label={t('proteins') + ": " + preview.proteins + "%"} />
                          <Chip size="small" label={t('sugars') + ": " + preview.sugars + "%"} />
                          {hasAlcohol && (
                            <Chip size="small" label={<strong>{t('alcohol') + ": " + preview.alcohol + "%"}</strong>} />
                          )}
                           {(hasSpecificSugars || hasAlcohol) && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="primary" display="block">
                                ✓ {t('enhanced_calculations') || 'Enhanced PAC/POD calculations using specific sugar data'}
                              </Typography>
                              {hasAlcohol && (
                                <Typography variant="caption" color="secondary" display="block">
                                  ⚡ {t('alcohol_contribution') || 'Alcohol contributes significantly to PAC/POD'}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                )
              }
              )}
            </List>
          </Box>
        )}

        {selectedFood && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('selected_food') || 'Selected Food'}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <strong>{selectedFood.description}</strong>
            </Typography>
            
            {selectedFood.brandOwner && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('brand') || 'Brand'}: {selectedFood.brandOwner}
              </Typography>
            )}

            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              {t('nutritional_preview') || 'Nutritional Preview'}:
            </Typography>
            
            {(() => {
              const preview = convertUSDAToIngredient(selectedFood);
              const hasSpecificSugars = preview._usda_details && preview._usda_details.sucrose + preview._usda_details.glucose + preview._usda_details.fructose > 0;
              const hasAlcohol = preview.alcohol > 0;
              
              return (
                <Box sx={{ ml: 2 }}>
                  <Typography variant="body2"><strong>• {t('water')}: {preview.water}%</strong></Typography>
                  <Typography variant="body2"><strong>• {t('fats')}: {preview.fats}%</strong></Typography>
                  <Typography variant="body2"><strong>• {t('proteins')}: {preview.proteins}%</strong></Typography>
                  <Typography variant="body2"><strong>• {t('sugars')}: {preview.sugars}%</strong></Typography>
                  
                  {hasAlcohol && (
                    <Typography variant="body2"><strong>• {t('alcohol')}: {preview.alcohol}%</strong></Typography>
                  )}

                  {hasSpecificSugars && (
                    <Box sx={{ ml: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('sugar_breakdown') || 'Sugar breakdown'}:
                      </Typography>
                      {preview._usda_details.sucrose > 0 && (
                        <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                          - {t('sucrose') || 'Sucrose'}: {preview._usda_details.sucrose}%
                        </Typography>
                      )}
                      {preview._usda_details.glucose > 0 && (
                        <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                          - {t('glucose') || 'Glucose'}: {preview._usda_details.glucose}%
                        </Typography>
                      )}
                      {preview._usda_details.fructose > 0 && (
                        <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                          - {t('fructose') || 'Fructose'}: {preview._usda_details.fructose}%
                        </Typography>
                      )}
                      {preview._usda_details.lactose > 0 && (
                        <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                          - {t('lactose')}: {preview._usda_details.lactose}%
                        </Typography>
                      )}
                      {preview._usda_details.maltose > 0 && (
                        <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                          - {t('maltose') || 'Maltose'}: {preview._usda_details.maltose}%
                        </Typography>
                      )}
                      {preview._usda_details.galactose > 0 && (
                        <Typography variant="caption" display="block" sx={{ ml: 1 }}>
                          - {t('galactose') || 'Galactose'}: {preview._usda_details.galactose}%
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <Typography variant="body2"><strong>• {t('total_solids')}: {preview.total_solids}%</strong></Typography>
                  <Typography variant="body2" color="primary"><strong>• PAC: {preview.pac}</strong></Typography>
                  <Typography variant="body2" color="primary"><strong>• POD: {preview.pod}</strong></Typography>
                  {preview.lactose > 0 && (
                    <Typography variant="body2"><strong>• {t('lactose')}: {preview.lactose}%</strong></Typography>
                  )}
                  {preview.msnf > 0 && (
                    <Typography variant="body2"><strong>• MSNF: {preview.msnf}%</strong></Typography>
                  )}
                  
                  {(hasSpecificSugars || hasAlcohol) && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="primary" display="block">
                        ✓ {t('enhanced_calculations') || 'Enhanced PAC/POD calculations using specific sugar data'}
                      </Typography>
                      {hasAlcohol && (
                        <Typography variant="caption" color="secondary" display="block">
                          ⚡ {t('alcohol_contribution') || 'Alcohol contributes significantly to PAC/POD'}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })()}

            <Button
              variant="outlined"
              onClick={() => setSelectedFood(null)}
              sx={{ mt: 2 }}
            >
              {t('back_to_results') || 'Back to Results'}
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!selectedFood}
        >
          {t('import_ingredient') || 'Import Ingredient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default USDAImporter;
